import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { activationService } from '../services/api';
import {
  Gauge, Loader2, RefreshCw, ChevronRight, CheckCircle2, Circle,
  ShieldCheck, Zap, Mail, MessageSquare, Bell,
} from 'lucide-react';

const SERVICE_ICON = { 'In-App Notifications': Bell, 'Email Delivery': Mail, 'SMS Delivery': MessageSquare };

const ActivationStatusPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await activationService.getQuoteLeadStatus()); }
    catch (e) { setError('Failed to load activation status.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const prog = data?.activation_progress;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="activation-status-page">
        <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/admin/dashboard" className="hover:text-gray-700" data-testid="activation-breadcrumb-home">Admin Panel</Link>
          <ChevronRight className="w-4 h-4 mx-1" /><span className="text-gray-900 font-medium">Activation Status</span>
        </nav>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Gauge className="w-7 h-7 text-blue-600" /> Activation Status</h1>
            <p className="text-gray-600 mt-1">Preview feature readiness for {data?.program || 'Quote & Lead Management'} — internal testing only. Production is not affected by these toggles.</p>
          </div>
          <button onClick={load} data-testid="activation-refresh-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>

        {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">{error}</div>}

        {data && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3" data-testid="activation-production-status">
            <ShieldCheck className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-gray-900">Production Status: <span className="text-slate-600">CONTROLLED HOLD — NOT DEPLOYED</span></p>
              <p className="text-xs text-gray-500 mt-0.5">The progress and flag states below reflect the <span className="font-medium">Preview</span> environment only (internal readiness). No feature is live to customers in production.</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24" data-testid="activation-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : data ? (
          <>
            <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="activation-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">Preview Feature Enablement</span>
                <span className="text-sm font-semibold text-gray-700" data-testid="activation-progress-label">{prog.enabled}/{prog.total} · {prog.phase}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${prog.percent}%` }} data-testid="activation-progress-bar" />
              </div>
              <p className="text-xs text-gray-500 mt-2">Lifecycle: <span className="font-medium text-gray-700">{data.lifecycle_status}</span></p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="activation-flags">
              <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900 flex items-center gap-2"><Zap className="w-4 h-4 text-blue-600" /> Feature Flag Status (activation order)</div>
              <ul className="divide-y divide-gray-50">
                {data.flags.map((f) => (
                  <li key={f.key} data-testid={`activation-flag-${f.key}`} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {f.enabled ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <Circle className="w-5 h-5 text-gray-300 shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{f.order}. {f.name}</p>
                        <p className="text-xs text-gray-400 truncate">{f.key}{f.dependencies?.length ? ` · depends: ${f.dependencies.join(', ')}` : ''}</p>
                        {f.enabled && (f.enabled_at || f.enabled_by) && (
                          <p className="text-xs text-emerald-600 mt-0.5" data-testid={`activation-flag-meta-${f.key}`}>
                            Enabled {f.enabled_at ? new Date(f.enabled_at).toLocaleString() : ''}{f.enabled_by ? ` · by ${f.enabled_by}` : ''}
                          </p>
                        )}
                        {!f.enabled && f.rolled_back_at && (
                          <p className="text-xs text-amber-600 mt-0.5" data-testid={`activation-flag-rollback-${f.key}`}>
                            Rolled back {new Date(f.rolled_back_at).toLocaleString()}{f.rolled_back_by ? ` · by ${f.rolled_back_by}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${f.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`} data-testid={`activation-flag-state-${f.key}`}>{f.enabled ? 'Enabled' : 'Disabled'}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="activation-services">
              <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">Live / Mocked Services</div>
              <ul className="divide-y divide-gray-50">
                {data.services.map((s) => {
                  const Icon = SERVICE_ICON[s.name] || Bell;
                  const live = s.mode === 'live';
                  return (
                    <li key={s.name} data-testid={`activation-service-${s.name.toLowerCase().replace(/\s+/g, '-')}`} className="px-5 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className="w-4 h-4 text-gray-400 shrink-0" />
                        <div className="min-w-0"><p className="text-sm font-medium text-gray-900">{s.name}</p><p className="text-xs text-gray-400 truncate">{s.detail}</p></div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${live ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{live ? 'Live' : 'Mocked'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-3" data-testid="activation-rollback">
              <ShieldCheck className="w-6 h-6 text-emerald-500 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Rollback Status: <span className="text-emerald-600">{data.rollback.status === 'ready' ? 'Ready — fully reversible' : data.rollback.status}</span></p>
                <p className="text-xs text-gray-500 mt-1">{data.rollback.note}</p>
                {data.last_rollback ? (
                  <p className="text-xs text-amber-600 mt-2" data-testid="activation-last-rollback">
                    Last rollback: <span className="font-medium">{data.last_rollback.name}</span> · {new Date(data.last_rollback.at).toLocaleString()}{data.last_rollback.by ? ` · by ${data.last_rollback.by}` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-2" data-testid="activation-last-rollback">Last rollback: none recorded.</p>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center">Read-only. Toggling flags requires super_admin via Feature Flags admin. Generated {new Date(data.generated_at).toLocaleString()}.</p>
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
};

export default ActivationStatusPage;
